---
layout: docs
title: "Deploy from Azure Marketplace"
---


Corral deploys into your Azure tenant from the Azure Marketplace as a Managed Application. The entire process takes under 30 minutes.

<div class="video-embed">
  <iframe
    src="https://player.vimeo.com/video/VIDEO_ID"
    title="Corral Azure Deployment Walkthrough"
    allow="fullscreen; picture-in-picture"
    allowfullscreen>
  </iframe>
</div>

---

## Prerequisites

- An Azure subscription with sufficient permissions to create resources
- The ability to grant admin consent for an Entra ID app registration (Global Admin, Application Admin, or Cloud Application Admin)

---

## What You'll Do

1. **Click Deploy** on the Azure Marketplace listing
2. **Fill in the configuration form** — instance name, organization name, contact email, naming preferences
3. **Bookmark the onboarding link** — the create wizard provides a tracking URL before you click Create
4. **Click Create** and wait briefly for the deployment to start — once the managed application icon appears in your portal, the onboarding page begins tracking progress
5. **Follow the onboarding page** — it walks you through each phase, including admin consent for Entra ID when it becomes available
6. **Log in** to your Corral instance

---

## What Happens Behind the Scenes

The deployment runs in phases. The onboarding page tracks each one in real time.

### Pre-phase: Resource Provider Registration

The marketplace create wizard automatically registers any Azure resource providers needed in your subscription before deployment begins.

### Phase 1: Bootstrap & Azure Setup (2–3 minutes)

The marketplace ARM template deploys lightweight bootstrap resources: a managed identity, a Key Vault with deployment metadata, and connects the resource group. Once this phase starts, the onboarding tracking link becomes active.

### Phase 2: App Registration Creation (minutes)

Creates the app registration for SSO authentication and configures access policies. Once this phase completes, admin consent becomes available on the onboarding page — click through to grant consent in Entra ID, and you'll be redirected back to continue tracking.

### Phase 3: Security Setup

Stores secrets and credentials in your instance's Key Vault.

### Phases 4–5: Infrastructure & Applications Deployment (the bulk of the time)

Deploys the full set of Azure resources into a managed resource group in your subscription, then deploys and configures the application containers — set up for future managed updates.

| Resource | Azure Service | Purpose |
|---|---|---|
| Managed Identity | User Assigned Managed Identity | Core identity for all services |
| Monitoring | Log Analytics Workspace | Centralized logging |
| Telemetry | Application Insights | Performance and usage telemetry |
| Secrets | Key Vault | Secrets, certificates, data protection keys |
| File Storage | Storage Account | State data, user files |
| Database | SQL Server + Database | All operational data |
| Compute | Container Apps Environment | Hosts all application containers |
| AI Models | AI Foundry Hub + Project | Model deployments |
| Static Assets | Static Web App | CDN for embedded widgets and static files |
| Code Execution | Session Pool | Sandboxed code execution for AI agents |
| Application Services | Container Apps (×4) | API server, Functions, document processing, database migrations |

### Phase 6: Configuration & Finalization (seconds)

Updates redirect URIs with actual deployed URLs and completes final configuration.

---

## After Deployment

Once provisioning completes (the onboarding page shows all phases as done):

1. **Admin consent** should already be complete — if you followed the onboarding page, you granted consent during Phase 2. If not, the onboarding page still provides the consent link.

2. **Log in**: Click the admin console link on the onboarding page. Sign in with your organizational account. You'll land as the first user with full permissions.

3. **Start using it**: See [Your First 30 Minutes →](./first-30-minutes) for what to do next.

<!-- TODO: ## Troubleshooting

Common deployment issues — resource provider registration failures, insufficient permissions, quota limits on Container Apps or SQL. Include the /onboarding status tracking site and how to use it to diagnose where a deployment stalled. -->
